import { useState } from "react";
import {
  Check,
  Clock,
  Code,
  Copy,
  File as FileIcon,
  FileText,
  Folder,
  HardDrive,
  Image as ImageIcon,
  Plus,
  Send,
  User as UserIcon,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCreateCollection } from "../hooks/useMedia";
import type { MediaCollection } from "../types";

type Props = {
  collections: MediaCollection[];
  activeKey: string;
  onSelect: (key: string) => void;
  totalFiles: number;
  recentCount: number;
  myCount?: number;
};

const ICONS: Record<string, typeof Folder> = {
  Folder,
  FileText,
  Code,
  Send,
  Image: ImageIcon,
  Copy,
};

function CollectionItem({
  icon: Icon,
  label,
  count,
  active,
  onClick,
}: {
  icon: typeof Folder;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center justify-between w-full rounded-md px-2 py-1.5 text-sm transition-colors",
        active ? "bg-primary text-primary-foreground" : "hover:bg-muted",
      )}
    >
      <span className="flex items-center gap-2 min-w-0">
        <Icon size={14} className="shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <span
        className={cn(
          "text-[10px] rounded px-1.5 py-0.5 shrink-0",
          active
            ? "bg-white/20 text-primary-foreground"
            : "bg-muted text-muted-foreground",
        )}
      >
        {count}
      </span>
    </button>
  );
}

export function CollectionsSidebar({
  collections,
  activeKey,
  onSelect,
  totalFiles,
  recentCount,
  myCount,
}: Props) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const createCollection = useCreateCollection();

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setCreating(false);
      return;
    }
    await createCollection.mutateAsync({ name: trimmed });
    setName("");
    setCreating(false);
  }

  return (
    <aside
      className="w-60 shrink-0 flex flex-col"
      style={{
        background: "white",
        border: "1px solid hsl(210,30%,85%)",
        borderRadius: "0.75rem",
        overflow: "hidden",
      }}
    >
      <div
        className="flex items-center justify-between px-3 h-10"
        style={{ borderBottom: "1px solid hsl(210,30%,85%)" }}
      >
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
          Collections
        </span>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="p-1 rounded hover:bg-muted"
          aria-label="New collection"
        >
          <Plus size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
        <CollectionItem
          icon={HardDrive}
          label="All Files"
          count={totalFiles}
          active={activeKey === "all"}
          onClick={() => onSelect("all")}
        />
        <CollectionItem
          icon={Clock}
          label="Recent"
          count={recentCount}
          active={activeKey === "recent"}
          onClick={() => onSelect("recent")}
        />
        <CollectionItem
          icon={UserIcon}
          label="My Uploads"
          count={myCount ?? 0}
          active={activeKey === "mine"}
          onClick={() => onSelect("mine")}
        />

        <div className="h-px my-2 bg-border" />

        {collections.map((c) => {
          const Icon = c.icon ? (ICONS[c.icon] ?? FileIcon) : FileIcon;
          return (
            <CollectionItem
              key={c.id}
              icon={Icon}
              label={c.name}
              count={c.fileCount}
              active={activeKey === `col:${c.id}`}
              onClick={() => onSelect(`col:${c.id}`)}
            />
          );
        })}

        {creating && (
          <div className="flex items-center gap-1 px-2 py-1">
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
                if (e.key === "Escape") {
                  setName("");
                  setCreating(false);
                }
              }}
              placeholder="Collection name"
              className="flex-1 text-sm border rounded px-2 py-1"
            />
            <button
              type="button"
              onClick={submit}
              className="p-1 rounded hover:bg-muted text-green-600"
              aria-label="Create"
            >
              <Check size={14} />
            </button>
            <button
              type="button"
              onClick={() => {
                setName("");
                setCreating(false);
              }}
              className="p-1 rounded hover:bg-muted"
              aria-label="Cancel"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
